import { assertNotEmpty, type Optional } from '@festina-life/core'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import type {
  SubTemplateFieldValue,
  SubTemplateValueSetupField,
  SupportedEditableTemplateFieldValue,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'
import { isInherited } from '../../uiModels/templateOperations/types'
import { InheritedValueCopier } from '../copyInheritedValue/InheritedValueCopier'
import { EditableMarkdownViewer } from '../markdownEditor/EditableMarkdownViewer'

type Props = {
  forField: SubTemplateValueSetupField
  variant: Optional<SubTemplateFieldValue>
}

export const SubtemplateVariantDocumentationEditor = ({
  variant,
  forField,
}: Props) => {
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  const documentation = variant?.documentation
  const subTemplatedField = variant?.subTemplatedField ?? forField

  assertNotEmpty(subTemplatedField)
  assertNotEmpty(templateVersion)

  const fieldValue =
    variant?.subTemplatedField?.value ??
    generateEmptyFieldValue({
      field: subTemplatedField,
      meta: metaModel,
    })

  const handleSaveDocumentation = (updatedDocumentation: Optional<string>) => {
    const currentVariants = [...(subTemplatedField.value?.variants ?? [])]

    if (currentVariants.length) {
      const changedVariant = currentVariants.find(
        (comparedVariant) =>
          comparedVariant.subTemplateIdentifier ===
          variant?.subTemplateIdentifier,
      )
      assertNotEmpty(changedVariant)

      const variantIndex = currentVariants.indexOf(changedVariant)
      const newVariant = { ...changedVariant }

      if (updatedDocumentation) {
        newVariant.documentation = {
          value: updatedDocumentation,
          origin: `${newVariant.path}.documentation`,
        }
        newVariant.origin = newVariant.path
      } else {
        delete newVariant.documentation
      }
      ;(
        subTemplatedField.update as (
          nextValue: SupportedEditableTemplateFieldValue,
        ) => void
      )({
        ...fieldValue,
        availableVariantTypes:
          subTemplatedField.value?.availableVariantTypes ?? [],
        variants: currentVariants.toSpliced(variantIndex, 1, newVariant),
      })
    }
  }

  const isInheritedDocumentation =
    !!documentation &&
    isInherited({
      origin: documentation?.origin,
      path: `${variant.path}.documentation`,
    })

  return (
    <div className="group relative">
      <EditableMarkdownViewer
        value={documentation?.value}
        onSave={
          isDraftTemplate && !isInheritedDocumentation
            ? handleSaveDocumentation
            : undefined
        }
      />
      {isInheritedDocumentation && (
        <InheritedValueCopier
          onCopy={() => handleSaveDocumentation(documentation?.value)}
          fromOrigin={documentation?.origin}
          fieldPath={`${variant.path}.documentation`}
        />
      )}
    </div>
  )
}
